import './styles/VoWifi_A.scss';

import { useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { StepWizard } from '../../../../components/StepWizard';
import { DEVICE_TYPES, VOWIFI_VERSIONS } from '../../../../constants';
import { cn } from '../../../../helpers/classBuilders';
import { parseError } from '../../../../helpers/errorUtils';
import { IServices, Maybe, SelectedFieldInPreview } from '../../../../types';
import { UnexpectedErrorWebsheet } from '../../../UnexpectedErrorWebsheet/UnexpectedErrorWebsheet';
import { DATADOG_ACTIONS } from '../../constants';
import { trackSdkCall } from '../../helpers/trackSdkCall';
import { useDataDogActions } from '../../hooks/useDataDogActions';
import { useGetStyles } from '../../hooks/useGetStyles';
import { AddressScreen } from './components/Address';
import { ConfirmationAddress } from './components/Confirmation';
import { Layout } from './components/Layout';
import { SplashScreen } from './components/Splash';
import { TermsConditionsScreen } from './components/TermsConditions';
import { useFormState } from './hooks/useFormState';
import { useGetUseCaseSettings } from './hooks/useGetUseCaseSettings';
import { IText, IValues } from './types';

type SharedProps = {
  values: IValues;
  services: IServices;
  selectedField: Maybe<SelectedFieldInPreview>;
  state: string;
  isPortalTest: boolean;
  portalMessenger: Maybe<any>;
  deviceSdk: Maybe<any>;
  deviceType?: (typeof DEVICE_TYPES)[keyof typeof DEVICE_TYPES];
};

type IsPortalProps = {
  values: IValues;
  text: Partial<IText>;
  isPortalPreview: true;
};

type IsNotPortalProps = {
  values: IValues;
  text: any;
  isPortalPreview: false;
};

type Props = SharedProps & (IsPortalProps | IsNotPortalProps);

export const VoWifiA = (props: Props) => {
  const { t } = useTranslation();

  const {
    values,
    isPortalPreview,
    selectedField,
    services,
    state,
    isPortalTest,
    portalMessenger,
    deviceSdk,
  } = props;

  const getStyles = useGetStyles({ values });

  const { dataDogLogger } = useDataDogActions({
    isPortalPreview,
    actionKey: 'vowifi',
  });

  const {
    requiresTc,
    requiresAddress,
    autocompleteEnabled,
    carrierCountry,
    isLoadingAddress,
    addressData,
    hasPrefilledAddress,
    settingsError,
  } = useGetUseCaseSettings({ values, services, state, isPortalPreview });

  const { setFormValues } = useFormState();

  useEffect(() => {
    if (addressData) {
      setFormValues(addressData);
    }
  }, [addressData, setFormValues]);

  const handleExit = () => {
    if (deviceSdk && deviceSdk.exit) {
      // exit() emits its own teardown actions; stop the session after
      // so those logs are captured in it.
      trackSdkCall('exit', () => deviceSdk.exit(), dataDogLogger);
      dataDogLogger.stopSession();
      return;
    }

    // The SDK method is missing — the user's close does nothing.
    // Report it so the silent failure is visible, then close the session.
    dataDogLogger.error(DATADOG_ACTIONS.errors.template_error, {
      flow: 'sdk_unavailable',
      errorCode: 'SDK_METHOD_MISSING',
      errorMessage: 'deviceSdk.exit is unavailable',
      method: 'exit',
    });
    dataDogLogger.stopSession();
  };

  useEffect(() => {
    if (settingsError && !isPortalPreview) {
      const { errorCode, errorMessage, statusCode } = parseError(settingsError);

      dataDogLogger.error(DATADOG_ACTIONS.errors.template_error, {
        flow: 'load_config',
        statusCode,
        errorCode: errorCode ?? 'UNKNOWN',
        errorMessage,
      });
    }
  }, [settingsError]);

  const renderContent = useCallback(() => {
    if (isLoadingAddress) {
      return <div />;
    }

    const { text } = props || {};

    const errorGuard = [
      { condition: !!settingsError, prefix: 'error' },
      {
        condition: !requiresAddress && !isPortalPreview,
        prefix: 'error.vowifi.address',
      },
    ].find(({ condition }) => condition);

    if (errorGuard) {
      return (
        <UnexpectedErrorWebsheet
          title={t(`${errorGuard.prefix}.title`)}
          body={t(`${errorGuard.prefix}.body`)}
          close={t(`${errorGuard.prefix}.close`)}
          onClick={handleExit}
        />
      );
    }

    // This section will only affect the portal preview to modify the device end, go to the return after this conditional
    if (isPortalPreview) {
      const textTemplateType = text?.type;

      const subPages = {
        [VOWIFI_VERSIONS['vowifi-splash']]: (
          <SplashScreen
            themeValues={values}
            isPortalPreview={isPortalPreview}
            title={text.title}
            subtitle={text.subtitle}
            confirm={text.confirm}
          />
        ),
        [VOWIFI_VERSIONS['vowifi-t&c']]: (
          <TermsConditionsScreen
            deviceSdk={deviceSdk}
            themeValues={values}
            isPortalPreview={isPortalPreview}
            title={text.title}
            content={text.content}
            confirm={text.confirm}
            reject={text.reject}
          />
        ),
        [VOWIFI_VERSIONS['vowifi-address']]: (
          <AddressScreen
            themeValues={values}
            carrierCountry={carrierCountry}
            autocompleteEnabled={autocompleteEnabled}
            services={services}
            isPortalPreview={isPortalPreview}
            title={text.title}
            subtitle={text.subtitle}
            content_address={text.content_address}
            submit={text.submit}
            address_1_label={text.address_1_label}
            address_2_label={text.address_2_label}
            city_label={text.city_label}
            state_label={text.state_label}
            zip_label={text.zip_label}
            state_placeholder={text.state_placeholder}
            is_required_label={text.is_required_label}
            selectedField={selectedField}
            address_1_placeholder={text.address_1_placeholder}
            address_2_placeholder={text.address_2_placeholder}
            city_placeholder={text.city_placeholder}
            zip_placeholder={text.zip_placeholder}
            find_address_label={text.find_address_label}
            find_address_placeholder={text.find_address_placeholder}
            no_results_label={text.no_results_label}
            street_number_label={text.street_number_label}
            street_number_placeholder={text.street_number_placeholder}
            psap_error_title={text.psap_error_title}
            psap_submit_label={text.psap_submit_label}
            suggestions_title={text.suggestions_title}
            suggestion_instruction={text.suggestion_instruction}
            you_entered_label={text.you_entered_label}
            suggested_address_label={text.suggested_address_label}
            suggestion_info_label={text.suggestion_info_label}
            use_selected_address={text.use_selected_address}
            use_address_as_entered={text.use_address_as_entered}
            suggestion_edit_address={text.suggestion_edit_address}
            unverified_title={text.unverified_title}
            unverified_instruction={text.unverified_instruction}
            use_address_anyway={text.use_address_anyway}
          />
        ),
        [VOWIFI_VERSIONS['vowifi-confirmation']]: (
          <ConfirmationAddress
            themeValues={values}
            state={undefined}
            isPortalPreview={isPortalPreview}
            title={text.title}
            subtitle={text.subtitle}
            confirm={text.confirm}
            editAddress={text.editAddress}
            selectedField={selectedField}
            success_title={text.success_title}
            success_subtitle={text.success_subtitle}
            error_title={text.error_title}
            error_subtitle={text.error_subtitle}
            loading_title={text.loading_title}
            loading_subtitle={text.loading_subtitle}
            emergency_address_validation_failed={
              text.emergency_address_validation_failed
            }
            address_help_label={text.address_help_label}
            done={text.done}
            close={text.close}
            services={services}
            isPortalTest={isPortalTest}
            portalMessenger={portalMessenger}
            deviceSdk={deviceSdk}
            deviceType={props.deviceType}
          />
        ),
      };

      return subPages[textTemplateType] || null;
    }

    return (
      <StepWizard variant="fullWidth">
        <SplashScreen
          themeValues={values}
          title={text?.['vowifi-splash']?.title}
          subtitle={text?.['vowifi-splash']?.subtitle}
          confirm={text?.['vowifi-splash']?.confirm}
          skipNextStep={hasPrefilledAddress && !requiresTc}
        />
        {requiresTc && (
          <TermsConditionsScreen
            themeValues={values}
            deviceSdk={deviceSdk}
            skipNextStep={hasPrefilledAddress}
            title={text?.['vowifi-t&c']?.title}
            content={text?.['vowifi-t&c']?.content}
            confirm={text?.['vowifi-t&c']?.confirm}
            reject={text?.['vowifi-t&c']?.reject}
          />
        )}
        {requiresAddress && (
          <AddressScreen
            themeValues={values}
            carrierCountry={carrierCountry}
            autocompleteEnabled={autocompleteEnabled}
            services={services}
            title={text?.['vowifi-address']?.title}
            subtitle={text?.['vowifi-address']?.subtitle}
            content_address={text?.['vowifi-address']?.content_address}
            submit={text?.['vowifi-address']?.submit}
            address_1_label={text?.['vowifi-address']?.address_1_label}
            address_2_label={text?.['vowifi-address']?.address_2_label}
            city_label={text?.['vowifi-address']?.city_label}
            state_label={text?.['vowifi-address']?.state_label}
            zip_label={text?.['vowifi-address']?.zip_label}
            state_placeholder={text?.['vowifi-address']?.state_placeholder}
            is_required_label={text?.['vowifi-address']?.is_required_label}
            address_1_placeholder={
              text?.['vowifi-address']?.address_1_placeholder
            }
            address_2_placeholder={
              text?.['vowifi-address']?.address_2_placeholder
            }
            city_placeholder={text?.['vowifi-address']?.city_placeholder}
            zip_placeholder={text?.['vowifi-address']?.zip_placeholder}
            find_address_label={text?.['vowifi-address']?.find_address_label}
            find_address_placeholder={
              text?.['vowifi-address']?.find_address_placeholder
            }
            no_results_label={text?.['vowifi-address']?.no_results_label}
            street_number_label={text?.['vowifi-address']?.street_number_label}
            street_number_placeholder={
              text?.['vowifi-address']?.street_number_placeholder
            }
            psap_error_title={text?.['vowifi-address']?.psap_error_title}
            psap_submit_label={text?.['vowifi-address']?.psap_submit_label}
            suggestions_title={text?.['vowifi-address']?.suggestions_title}
            suggestion_instruction={
              text?.['vowifi-address']?.suggestion_instruction
            }
            you_entered_label={text?.['vowifi-address']?.you_entered_label}
            suggested_address_label={
              text?.['vowifi-address']?.suggested_address_label
            }
            suggestion_info_label={
              text?.['vowifi-address']?.suggestion_info_label
            }
            use_selected_address={
              text?.['vowifi-address']?.use_selected_address
            }
            use_address_as_entered={
              text?.['vowifi-address']?.use_address_as_entered
            }
            suggestion_edit_address={
              text?.['vowifi-address']?.suggestion_edit_address
            }
            unverified_title={text?.['vowifi-address']?.unverified_title}
            unverified_instruction={
              text?.['vowifi-address']?.unverified_instruction
            }
            use_address_anyway={text?.['vowifi-address']?.use_address_anyway}
          />
        )}
        {requiresAddress && (
          <ConfirmationAddress
            themeValues={values}
            services={services}
            state={state}
            isPortalTest={isPortalTest}
            portalMessenger={portalMessenger}
            deviceSdk={deviceSdk}
            deviceType={props.deviceType}
            title={text?.['vowifi-confirmation']?.title}
            subtitle={text?.['vowifi-confirmation']?.subtitle}
            confirm={text?.['vowifi-confirmation']?.confirm}
            editAddress={text?.['vowifi-confirmation']?.editAddress}
            close={text?.['vowifi-confirmation']?.close}
            selectedField={selectedField}
            success_title={text?.['vowifi-confirmation']?.success_title}
            success_subtitle={text?.['vowifi-confirmation']?.success_subtitle}
            done={text?.['vowifi-confirmation']?.done}
            error_title={text?.['vowifi-confirmation']?.error_title}
            error_subtitle={text?.['vowifi-confirmation']?.error_subtitle}
            loading_title={text?.['vowifi-confirmation']?.loading_title}
            loading_subtitle={text?.['vowifi-confirmation']?.loading_subtitle}
            emergency_address_validation_failed={
              text?.['vowifi-confirmation']?.emergency_address_validation_failed
            }
            address_help_label={
              text?.['vowifi-confirmation']?.address_help_label
            }
          />
        )}
      </StepWizard>
    );
  }, [
    isPortalPreview,
    isLoadingAddress,
    props,
    selectedField,
    values,
    carrierCountry,
    services,
    state,
    isPortalTest,
    portalMessenger,
    deviceSdk,
    requiresTc,
    requiresAddress,
    autocompleteEnabled,
    addressData,
    hasPrefilledAddress,
    settingsError,
  ]);

  return (
    <Layout
      getStyles={getStyles}
      isPreview={isPortalPreview || isPortalTest}
      className={cn({
        'p-0!': settingsError || (!requiresAddress && !isPortalPreview),
      })}
    >
      {renderContent()}
    </Layout>
  );
};
